import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth, hasPermission } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard,
  Users,
  Building2,
  UserCog,
  LogOut,
  Menu,
  TrendingUp,
  BarChart3,
  Package,
  BookOpen,
  Activity,
  FileText,
} from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import TopBar from "@/components/TopBar";
import AlertBanner from "@/components/AlertBanner";

const NAV = [
  { to: "/", label: "Panou", icon: LayoutDashboard, testid: "nav-dashboard", end: true, perm: "dashboard" },
  { to: "/analiza", label: "Dashboard Analiză", icon: Activity, testid: "nav-analytics", perm: "analiza" },
  { to: "/clienti", label: "Clienți", icon: Building2, testid: "nav-clients", perm: "clients" },
  { to: "/contracte", label: "Contracte", icon: FileText, testid: "nav-contracts", perm: "contracts" },
  { to: "/fise", label: "Fișă de Magazie", icon: BookOpen, testid: "nav-fise", perm: "fise" },
  { to: "/stocuri", label: "Stocuri", icon: Package, testid: "nav-stocks", perm: "stocuri" },
  { to: "/vanzari", label: "Vânzări", icon: TrendingUp, testid: "nav-sales", perm: "sales" },
  { to: "/rapoarte", label: "Rapoarte agenți", icon: BarChart3, testid: "nav-reports", perm: "reports" },
  { to: "/utilizatori", label: "Utilizatori", icon: UserCog, testid: "nav-users", adminOnly: true },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const items = NAV.filter((n) => {
    if (n.adminOnly) return user?.role === "admin";
    if (n.perm) return hasPermission(user, n.perm);
    return true;
  });

  const handleLogout = async () => {
    await logout();
    nav("/login", { replace: true });
  };

  const SidebarInner = (
    <>
      <div className="p-6 border-b border-border">
        <Link to="/" className="flex items-center gap-3">
          <img src="/logo.png" alt="IMAGGIO 2020 TIM" className="w-11 h-11 object-contain" />
          <div>
            <p className="font-brand text-base leading-tight text-gold-deep" style={{ letterSpacing: "0.08em" }}>IMAGGIO</p>
            <p className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mt-0.5">2020 TIM SRL</p>
          </div>
        </Link>
      </div>
      <nav className="p-3 flex-1">
        {items.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.end}
            onClick={() => setOpen(false)}
            data-testid={it.testid}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors",
                isActive
                  ? "bg-gold-soft text-gold-deep border-l-2 border-gold pl-[10px]"
                  : "text-foreground hover:bg-gold-soft/50"
              )
            }
          >
            <it.icon className="w-4 h-4" />
            {it.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-4 border-t border-border">
        <div className="mb-3">
          <p className="label-eyebrow">Conectat ca</p>
          <p className="text-sm font-medium mt-1 truncate" data-testid="current-user-name">{user?.name}</p>
          <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
          <span className="mt-2 inline-block text-[10px] uppercase tracking-widest border border-border px-2 py-0.5 rounded-sm">
            {user?.role === "admin" ? "Administrator" : "Utilizator"}
          </span>
        </div>
        <Button
          variant="outline"
          className="w-full justify-start gap-2"
          onClick={handleLogout}
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4" /> Deconectare
        </Button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-72 flex-col bg-white border-r border-border sticky top-0 h-screen">
        {SidebarInner}
      </aside>

      {/* Mobile sidebar */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-72 bg-white flex flex-col border-r border-border" data-testid="mobile-sidebar">
            {SidebarInner}
          </div>
          <div className="flex-1 bg-slate-900/40 backdrop-blur-sm" onClick={() => setOpen(false)} />
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile header */}
        <header className="lg:hidden sticky top-0 z-40 bg-white border-b border-border px-4 h-14 flex items-center justify-between">
          <button
            onClick={() => setOpen(true)}
            className="p-2 -ml-2 rounded-md hover:bg-slate-100 transition-colors"
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-5 h-5" />
          </button>
          <p className="font-brand text-sm text-gold-deep" style={{ letterSpacing: "0.08em" }}>IMAGGIO 2020 TIM SRL</p>
          <div className="w-9" />
        </header>

        <TopBar />
        <AlertBanner />
        <main className="flex-1 p-4 sm:p-8 max-w-[1400px] w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
